import { takeLatest, put, take, race, delay } from "redux-saga/effects";
import i18next from "i18next";
import { GET_DATA, POST_DATA } from "actions/apiOperations.js";
import { addNotification } from "notificationStore";
import { 
  barcodeQtyImportActions,
  requestFileUpload,
  buyplanBarcodeQtyImportRequest,
  barcodeQtyImportStatusRequest
} from "actions/barcodeQtyImportActions.js";
import { redirectToURL } from "apis/apiHelpers";


export const requestFileUploadUrl = function*(action) {
  const params = action.payload;
  const url = params['stock_model_id']
    ? `/api/stock_models/${params['stock_model_id']}/file_upload_url.json`
    : `/api/buyplans/${params['buyplan_id']}/file_upload_url.json`

  yield put({
    type: GET_DATA,
    path: url,
    actions: {
      success: barcodeQtyImportActions.fileUploadUrlSuccess,
      failure: barcodeQtyImportActions.fileUploadUrlFailure
    },
    params
  });

  const { success, failure } = yield race({
    success: take(barcodeQtyImportActions.fileUploadUrlSuccess),
    failure: take(barcodeQtyImportActions.fileUploadUrlFailure)
  });

  if (success) {
    params["presignedUrl"] = success.payload.data

    yield put(requestFileUpload(params));
  }

  if (failure) {
    addNotification({ type: "danger", message: i18next.t("barcode.fileUploadFailed") });
  }
};

export const requestFileUploadAsync = function*(action) {
  const { presignedUrl, file, ...params } = action.payload;

  try {
    const response = yield fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (response.ok) {
      yield put(buyplanBarcodeQtyImportRequest(params))
    }
    else {
      addNotification({ type: "danger", message: i18next.t("barcode.fileUploadFailed") });
    }
  } catch (error) {
    // eslint-disable-next-line
    console.error('Upload error:', error);
    addNotification({ type: "danger", message: i18next.t("barcode.fileUploadFailed") });
  }
};

export const requestBuyplanBarcodeQtyImportAsync = function*(action) {
  const params = action.payload;
  const url = params['stock_model_id']
    ? `/api/stock_models/${params['stock_model_id']}/items/barcode_qty_import.json`
    : `/api/buyplans/${params['buyplan_id']}/items/barcode_qty_import.json`;

  yield put({
    type: POST_DATA,
    path: url,
    actions: {
      success: barcodeQtyImportActions.buyplanBarcodeQtyImportSuccess,
      failure: barcodeQtyImportActions.buyplanBarcodeQtyImportFailure
    },
    params
  });

  const { success, failure } = yield race({
    success: take(barcodeQtyImportActions.buyplanBarcodeQtyImportSuccess),
    failure: take(barcodeQtyImportActions.buyplanBarcodeQtyImportFailure)
  });

  if (success) {
    addNotification({ message: i18next.t("barcode.fileUploadSuccess") });
    const eventLogId = success?.payload?.data?.event_log_id;
    if (eventLogId) {
      yield put(barcodeQtyImportStatusRequest({ ...params, event_log_id: eventLogId }));
    }
  }
  if (failure) {
    addNotification({ type: "danger", message: i18next.t("barcode.fileUploadFailed") });
  }
};

export const requestBarcodeQtyImportStatusAsync = function*(action) {
  const params = action.payload;
  const { event_log_id } = params;
  if (!event_log_id) return;

  const statusUrl = params["stock_model_id"]
    ? `/api/stock_models/${params["stock_model_id"]}/items/barcode_qty_import_status/${event_log_id}.json`
    : `/api/buyplans/${params["buyplan_id"]}/items/barcode_qty_import_status/${event_log_id}.json`;

  while (true) {
    yield put({
      type: GET_DATA,
      path: statusUrl,
      actions: {
        success: barcodeQtyImportActions.barcodeQtyImportStatusSuccess,
        failure: barcodeQtyImportActions.barcodeQtyImportStatusFailure
      },
      params
    });

    const { success, failure } = yield race({
      success: take(barcodeQtyImportActions.barcodeQtyImportStatusSuccess),
      failure: take(barcodeQtyImportActions.barcodeQtyImportStatusFailure)
    });

    if (failure) return;

    const statusData = success?.payload?.data;
    const status = statusData?.status;
    const stage = statusData?.stage;

    if (status === "failed") return;

    if (status === "success" || stage === "upload_complete") {
      yield put({
        type: barcodeQtyImportActions.barcodeQtyImportCompleted,
        payload: { data: statusData }
      });

      const redirectUrl = statusData?.redirect_url;
      if (redirectUrl) {
        yield delay(250);
        try {
          const target = new URL(redirectUrl, window.location.origin);
          const current = new URL(window.location.href);
          const isSamePage =
            target.origin === current.origin &&
            target.pathname === current.pathname &&
            target.search === current.search;

          if (isSamePage) {
            // Ensure we land on the Buy Plan tab, then force reload to refetch data.
            if (target.hash) window.location.hash = target.hash;
            window.location.reload();
            return;
          }
        } catch {
          // Fall back to full navigation below.
        }

        yield redirectToURL(redirectUrl);
      }
      return;
    }

    yield delay(1000);
  }
};

export const watchRequestFileUploadUrl = function*() {
  yield takeLatest(barcodeQtyImportActions.requestFileUploadUrl, requestFileUploadUrl);
};

export const watchRequestFileUpload = function*() {
  yield takeLatest(barcodeQtyImportActions.requestFileUpload, requestFileUploadAsync);
};

export const watchBuyplanBarcodeQtyImportRequest = function*() {
  yield takeLatest(barcodeQtyImportActions.buyplanBarcodeQtyImportRequest, requestBuyplanBarcodeQtyImportAsync);
};

export const watchBarcodeQtyImportStatusRequest = function*() {
  yield takeLatest(barcodeQtyImportActions.barcodeQtyImportStatusRequest, requestBarcodeQtyImportStatusAsync);
};
